// frontend/src/hooks/useExclusions.ts - NEW EXCLUSION-SPECIFIC HOOKS
// Entity-specific hooks for exclusions separated from core useApi.ts
// Insert this as a new file: useExclusions.ts

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // Exclusion types
  Exclusion,
  ExclusionQueryParams,
  ExclusionCreateRequest,
  ExclusionUpdateRequest,
  ExclusionTestRequest,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Extended exclusion creation data with additional features
 */
interface ExtendedExclusionCreateRequest extends ExclusionCreateRequest {
  scope?: 'organization' | 'user';
  conditions?: {
    senderDomain?: string[];
    sender?: string[];
    subject?: string[];
    body?: string[];
    attachmentName?: string[];
    attachmentContent?: string[];
  };
  metadata?: {
    createdBy?: string;
    source?: 'manual' | 'imported' | 'auto-generated';
    category?: string;
  };
}

/**
 * Extended exclusion update data
 */
interface ExtendedExclusionUpdateRequest extends ExclusionUpdateRequest {
  preserveHistory?: boolean;
  updateReason?: string;
  scope?: 'organization' | 'user';
  conditions?: {
    senderDomain?: string[];
    sender?: string[];
    subject?: string[];
    body?: string[];
    attachmentName?: string[];
    attachmentContent?: string[];
  };
}

/**
 * Exclusion test result interface
 */
interface ExclusionTestResult {
  pattern: string;
  isRegex: boolean;
  testData: string[];
  results: Array<{
    input: string;
    matches: boolean;
    explanation?: string;
  }>;
  totalTests: number;
  matchCount: number;
  performance?: {
    executionTime: number;
    averageTime: number;
  };
}

/**
 * Exclusion statistics interface
 */
interface ExclusionStats {
  totalExclusions: number;
  activeExclusions: number;
  inactiveExclusions: number;
  byType: Record<string, number>;
  byScope: {
    organization: number;
    user: number;
  };
  matchStatistics: {
    totalMatches: number;
    matchesToday: number;
    averageMatchesPerDay: number;
  };
  performanceMetrics: {
    averageProcessingTime: number;
    fastestRule: string;
    slowestRule: string;
  };
}

/**
 * Bulk exclusion operation request
 */
interface BulkExclusionOperation {
  action: 'activate' | 'deactivate' | 'delete' | 'update';
  exclusionIds: string[];
  data?: Partial<ExclusionUpdateRequest>;
  reason?: string;
}

// ===== EXCLUSION QUERY HOOKS =====

/**
 * Fetch paginated list of exclusions with comprehensive filtering
 */
export const useExclusions = (params?: ExclusionQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Exclusion>> => {
      
      const response = await apiClient.exclusions.list(params);
      
      return normalizePaginatedResponse<Exclusion>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'exclusions_list',
      ...options
    }
  );
};

/**
 * Fetch exclusions by organization with proper scoping
 */
export const useExclusionsByOrganization = (organizationId?: string, params?: ExclusionQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Exclusion>> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      const response = await apiClient.exclusions.listByOrganization(organizationId!, params);
      
      return normalizePaginatedResponse<Exclusion>(response);
    },
    {
      immediate: !!organizationId,
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `exclusions_org_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch single exclusion by ID with comprehensive details
 */
export const useExclusion = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Exclusion> => {
      validateObjectId(id, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.get(id!);
      
      return normalizeSingleResponse<Exclusion>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `exclusion_${id}`,
      ...options
    }
  );
};

/**
 * Search exclusions with advanced filtering
 */
export const useSearchExclusions = (query: string, filters?: Partial<ExclusionQueryParams>, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Exclusion>> => {
      if (!query || query.trim().length === 0) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      const response = await apiClient.exclusions.search(query, filters);
      
      return normalizePaginatedResponse<Exclusion>(response);
    },
    {
      immediate: !!query && query.trim().length > 0,
      dependencies: [query, JSON.stringify(filters || {})],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'exclusions_search',
      ...options
    }
  );
};

// ===== EXCLUSION MUTATION HOOKS =====

/**
 * Create new exclusion with comprehensive validation
 */
export const useCreateExclusion = (options?: UseMutationOptions<Exclusion, ExtendedExclusionCreateRequest>) => {
  return useMutation(
    async (data: ExtendedExclusionCreateRequest): Promise<Exclusion> => {
      // Validate required fields
      if (!data.name?.trim()) {
        throw new Error('Exclusion name is required');
      }
      
      // Validate conditions (new structure)
      if (data.conditions) {
        const hasConditions = Object.values(data.conditions).some(arr => arr && arr.length > 0);
        if (!hasConditions) {
          throw new Error('At least one condition is required');
        }
      } else {
        // For older API structure compatibility - only validate if conditions not provided
        if (!data.type) {
          throw new Error('Either type/pattern or conditions must be provided');
        }

        // Validate pattern if using old structure
        if (!data.pattern?.trim()) {
          throw new Error('Pattern is required when type is specified');
        }
      }
      
      
      const response = await apiClient.exclusions.create(data);
      
      return normalizeSingleResponse<Exclusion>(response);
    },
    options
  );
};

/**
 * Update existing exclusion with comprehensive validation
 */
export const useUpdateExclusion = (options?: UseMutationOptions<Exclusion, { id: string; data: ExtendedExclusionUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<Exclusion> => {
      validateObjectId(id, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.update(id, data);
      
      return normalizeSingleResponse<Exclusion>(response);
    },
    options
  );
};

/**
 * Delete exclusion with confirmation
 */
export const useDeleteExclusion = (options?: UseMutationOptions<any, string>) => {
  return useMutation(
    async (id: string) => {
      validateObjectId(id, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.delete(id);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Test exclusion pattern against sample data
 */
export const useTestExclusion = (options?: UseMutationOptions<ExclusionTestResult, ExclusionTestRequest>) => {
  return useMutation(
    async (testData: ExclusionTestRequest): Promise<ExclusionTestResult> => {
      // Validate test request
      if (!testData.pattern?.trim()) {
        throw new Error('Pattern is required for testing');
      }
      
      if (!testData.testData || testData.testData.length === 0) {
        throw new Error('Test data is required');
      }
      
      
      const response = await apiClient.exclusions.test(testData);
      
      // Transform response to match interface - handle StandardResponse structure
      const responseData = normalizeSingleResponse(response);
      
      const result: ExclusionTestResult = {
        pattern: testData.pattern,
        isRegex: testData.isRegex || false,
        testData: testData.testData,
        results: responseData.results || [],
        totalTests: testData.testData.length,
        matchCount: responseData.matchCount || 0,
        performance: responseData.performance
      };
      
      return result;
    },
    options
  );
};

// ===== EXCLUSION MANAGEMENT HOOKS =====

/**
 * Activate exclusion
 */
export const useActivateExclusion = (options?: UseMutationOptions<Exclusion, string>) => {
  return useMutation(
    async (id: string): Promise<Exclusion> => {
      validateObjectId(id, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.activate(id);
      
      return normalizeSingleResponse<Exclusion>(response);
    },
    options
  );
};

/**
 * Deactivate exclusion
 */
export const useDeactivateExclusion = (options?: UseMutationOptions<Exclusion, string>) => {
  return useMutation(
    async (id: string): Promise<Exclusion> => {
      validateObjectId(id, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.deactivate(id);
      
      return normalizeSingleResponse<Exclusion>(response);
    },
    options
  );
};

// ===== BULK OPERATIONS =====

/**
 * Bulk exclusion operations (activate, deactivate, delete, update)
 */
export const useBulkExclusionOperations = (options?: UseMutationOptions<any, BulkExclusionOperation>) => {
  return useMutation(
    async (operation: BulkExclusionOperation) => {
      // Validate operation
      if (!operation.exclusionIds || operation.exclusionIds.length === 0) {
        throw new Error('At least one exclusion ID is required');
      }
      
      validateObjectIds(operation.exclusionIds, 'Exclusion IDs');
      
      if (!['activate', 'deactivate', 'delete', 'update'].includes(operation.action)) {
        throw new Error('Invalid operation action');
      }
      
      
      let response;
      
      switch (operation.action) {
        case 'activate':
        case 'deactivate':
        case 'update':
          response = await apiClient.exclusions.bulkUpdate({
            operation: operation.action,
            ids: operation.exclusionIds,
            options: operation.data ? { data: operation.data } : undefined
          });
          break;
        case 'delete':
          response = await apiClient.exclusions.bulkDelete({
            operation: operation.action,
            ids: operation.exclusionIds
          });
          break;
        default:
          throw new Error(`Unsupported bulk operation: ${operation.action}`);
      }
      
      
      return response;
    },
    options
  );
};

// ===== ANALYTICS AND STATISTICS HOOKS =====

/**
 * Get exclusion statistics for an individual exclusion
 */
export const useExclusionStats = (exclusionId?: string, options?: UseApiOptions) => {
  return useApi(
    async () => {
      validateObjectId(exclusionId, 'Exclusion ID');
      
      
      const response = await apiClient.exclusions.getStats(exclusionId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!exclusionId,
      dependencies: [exclusionId],
      rateLimitKey: `exclusion_stats_${exclusionId}`,
      ...options
    }
  );
};

/**
 * Get comprehensive exclusion analytics
 */
export const useExclusionAnalytics = (params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<ExclusionStats> => {
      
      const response = await apiClient.exclusions.getAnalytics(params);
      
      return normalizeSingleResponse<ExclusionStats>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'exclusion_analytics',
      ...options
    }
  );
};

// ===== SPECIALIZED UTILITY HOOKS =====

/**
 * Hook for exclusion form validation
 */
export const useExclusionValidation = () => {
  const validateExclusionData = useCallback((data: Partial<ExtendedExclusionCreateRequest>): { valid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    
    // Name validation
    if (!data.name?.trim()) {
      errors.name = 'Exclusion name is required';
    } else if (data.name.length < 3) {
      errors.name = 'Name must be at least 3 characters long';
    }
    
    // Type/pattern validation (old structure)
    if (data.type && !data.pattern?.trim()) {
      errors.pattern = 'Pattern is required when type is specified';
    }
    
    // Conditions validation (new structure)
    if (data.conditions) {
      const hasConditions = Object.values(data.conditions).some(arr => arr && arr.length > 0);
      if (!hasConditions) {
        errors.conditions = 'At least one condition is required';
      }
    }
    
    // Either old or new structure required
    if (!data.type && !data.conditions) {
      errors.structure = 'Either type/pattern or conditions must be provided';
    }
    
    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }, []);
  
  const validatePattern = useCallback((pattern: string, isRegex: boolean = false): { valid: boolean; error?: string } => {
    if (!pattern.trim()) {
      return { valid: false, error: 'Pattern cannot be empty' };
    }
    
    if (isRegex) {
      try {
        new RegExp(pattern);
      } catch (e) {
        return { valid: false, error: 'Invalid regular expression pattern' };
      }
    }
    
    return { valid: true };
  }, []);
  
  return {
    validateExclusionData,
    validatePattern
  };
};